/**
 * The one place a nonprofit's brand palette becomes CSS custom properties (CON-5).
 *
 * Framework-free and dependency-free on purpose: the React wrapper lives in
 * `./BrandTokenProvider.jsx` (subpath `@charitystack/design-tokens/provider`,
 * React peer dep), and `packages/shared` binds the HSB expander to it. Nothing
 * here imports React, and nothing here imports `packages/shared`.
 *
 * WHAT IT WRITES — the union of the four writers the audit found
 * (docs/initiatives/new/design-system/audits/current-state.md §2), ten
 * properties:
 *
 *   --brand-60/50/40/20/10/5      the ramp (unchanged names: checkout, hosted-page
 *                                 and portal tailwind configs map `brand.*` to them,
 *                                 and portal writes them as inline styles)
 *   --cs-color-primary            = accent-50
 *   --cs-color-primary-foreground = deriveBrandRoles(accent-50).foreground (CON-512)
 *   --cs-color-primary-dark       = accent-60
 *   --cs-brand-text               = brandTextFor(the whole palette)
 *   --cs-control-hover-bg         = accent-10                       <- CON-157
 *
 * CON-157's row is the eleventh and the only one added since, and the reason it
 * has to be written HERE rather than in a value file is a substitution rule
 * worth stating once: a `var()` inside a custom property is substituted on the
 * element that DECLARES the property. `donor.css` declares
 * `--cs-control-hover-bg: var(--brand-10, var(--cs-surface-2))` at `:root`, so
 * that read resolves `--brand-10` AT `:root` — where this provider deliberately
 * never writes (D6) — and the neutral fallback is what every descendant
 * inherits. Measured in Chromium; it is CON-39's `@theme` versus `@theme
 * inline` finding one tier up. A dial that must carry brand therefore has to be
 * emitted on the same scope element as the ramp, by this writer, which is what
 * the row above does. The value-file declaration stays and is now doing its
 * real job: it is the value a surface with NO provider computes.
 *
 * A note on the `primary-dark` name, because it misleads (design.md §6.10, second
 * bullet): the ramp's `accent-60` is not a DARKENING of the brand, it is the
 * brand's hue re-emitted at a pinned saturation and brightness. For a near-black
 * brand it comes out LIGHTER than `accent-50`, and for a near-neutral brand it
 * comes out markedly more saturated than anything the nonprofit chose. It is
 * "the ramp's contrasting step", which is what a pressed state actually needs;
 * "dark" is wrong for a third of real inputs. The name is a live contract read
 * by shipped stylesheets, so CON-10 corrects the documentation rather than the
 * identifier.
 *
 * The `--cs-` prefix on the last three is CON-36's rename, not a new decision.
 * Renaming the ramp itself to `--cs-brand-*` (design/tokens/src/contract.md's
 * eventual contract) is CON-20's wiring, NOT this issue: the plan's step 6 pins
 * `--brand-*` because live Tailwind configs and portal inline styles read it.
 *
 * WHAT IT WILL NOT DO:
 *   - write `document.documentElement` (D6 — a global write silently repaints
 *     hosted-page's unsubscribe page, whose CSS fallback chain
 *     `var(--brand-50, var(--sage))` only fires while the variable is unset)
 *   - write a key it does not recognise. Checkout's palette arrives off the
 *     DONATE postMessage wire, not from the generator, so it is untrusted input.
 *   - write a step whose value is missing or is not a string. The old writers
 *     passed `undefined` straight into `setProperty`, which registers the literal
 *     token `undefined` and makes every `var()` read of it invalid at computed-value
 *     time (the fallback arm does NOT fire for a property that exists). See the
 *     record doc's "transient" note.
 */

import {
  brandTextForeground,
  colorChannels,
  contrastForeground,
  contrastRatio,
} from './contrast.js';

/* ── Donor redesign brand rule (CON-512) ──────────────────────────────────────
 * One org colour → the roles every donor surface paints. The rule is the one in
 * docs/initiatives/new/donor-experience-redesign/SPEC.md §A + §F1:
 *   1. action: if white text on the org colour reaches 3:1, darken it (HSL
 *      lightness, hue and saturation kept, 0.5% steps) until white reaches
 *      4.8:1; otherwise keep the org colour unchanged.
 *      1b. white scores at least BRAND_WHITE_LABEL_FLOOR but under 3:1: keep the
 *      org colour unchanged and use a white label (owner call 2026-11-03).
 *   2. foreground: white when the action passes with white or falls under 1b, else
 *      near-black.
 *   3. hover / pressed: action lightness −4 / −8 points.
 *   4. tint: 6% of the ORIGINAL org colour over white.
 *   5. accent (selected borders, focus, progress, chart) and brand text: the
 *      action when the label is white, else near-black (light brands).
 *   6. no usable brand: action near-black, white label, neutral tint.
 *   7. selected choice tile (owner call 2026-10-23): its edge is the action,
 *      always — light brands too, so a selected amount matches the Give
 *      button — and its fill is 12% of the action over white, strong enough
 *      that a dark brand reads as its colour rather than grey. The 2px edge and
 *      medium weight carry the selected state alongside the colour. */
export const BRAND_NEAR_BLACK = '#18181B';
// Rule 1b (owner call 2026-11-03): a brand where white scores at least this, but under
// 3:1, keeps its exact colour and takes a WHITE label. Oranges, ambers, golds and light
// blues read as the nonprofit's colour only with white; the owner accepted that these
// labels sit below the WCAG minimum. Under this floor (true yellows, limes, pastels)
// white is illegible and the label stays near-black.
export const BRAND_WHITE_LABEL_FLOOR = 2;
const BRAND_NO_BRAND_TINT = '#F7F7F8';
const BRAND_PAGE_GROUND = '#F7F7F8';
// How much of the action a selected tile's fill carries (rule 7).
const SELECTED_TINT_WEIGHT = 0.12;
const WHITE = '#FFFFFF';

function rgbToHsl([r, g, b]) {
  const [rn, gn, bn] = [r / 255, g / 255, b / 255];
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0);
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;
  return [h / 6, s, l];
}

function hslToRgb([h, s, l]) {
  if (!s) return [l * 255, l * 255, l * 255];
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (t) => {
    let x = t;
    if (x < 0) x += 1;
    if (x > 1) x -= 1;
    if (x < 1 / 6) return p + (q - p) * 6 * x;
    if (x < 1 / 2) return q;
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
    return p;
  };
  return [channel(h + 1 / 3), channel(h), channel(h - 1 / 3)].map((v) => v * 255);
}

function toHex(rgb) {
  return (
    '#' +
    rgb
      .map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase()
  );
}

function shiftLightness(rgb, delta) {
  const [h, s, l] = rgbToHsl(rgb);
  return toHex(hslToRgb([h, s, Math.max(0, Math.min(1, l + delta))]));
}

/**
 * The donor redesign brand roles for one organisation colour.
 * @param {string | null | undefined} orgColor Any colour `colorChannels` can read.
 * @returns {{ action: string, hover: string, pressed: string, foreground: string,
 *   tint: string, accent: string, text: string, darkLabel: boolean }}
 */
export function deriveBrandRoles(orgColor) {
  let org;
  try {
    let input = typeof orgColor === 'string' ? orgColor.trim() : '';
    // #RRGGBBAA → #RRGGBB: an admin colour's alpha has no meaning on a solid fill.
    if (/^#[0-9a-f]{8}$/i.test(input)) input = input.slice(0, 7);
    org = input ? colorChannels(input).slice(0, 3) : null;
  } catch {
    org = null;
  }
  if (!org) {
    return {
      action: BRAND_NEAR_BLACK,
      hover: shiftLightness(colorChannels(BRAND_NEAR_BLACK).slice(0, 3), -0.04),
      pressed: shiftLightness(colorChannels(BRAND_NEAR_BLACK).slice(0, 3), -0.08),
      foreground: WHITE,
      tint: BRAND_NO_BRAND_TINT,
      accent: BRAND_NEAR_BLACK,
      text: BRAND_NEAR_BLACK,
      selected: BRAND_NEAR_BLACK,
      selectedTint: BRAND_NO_BRAND_TINT,
      darkLabel: false,
    };
  }
  const orgHex = toHex(org);
  // Hover and pressed shift the UNROUNDED lightness the darkening loop ended
  // on, so they match Paper's values to the unit (rounding to hex first drifts
  // by one).
  const [h, s, startL] = rgbToHsl(org);
  let l = startL;
  let action = orgHex;
  let darkLabel = false;
  const whiteOnOrg = contrastRatio(WHITE, orgHex);
  if (whiteOnOrg >= 3) {
    while (contrastRatio(WHITE, action) < 4.8 && l > 0) {
      l = Math.max(0, l - 0.005);
      action = toHex(hslToRgb([h, s, l]));
    }
  } else if (whiteOnOrg < BRAND_WHITE_LABEL_FLOOR) {
    darkLabel = true;
  }
  const tint = toHex(org.map((v) => 255 + (v - 255) * 0.06));
  const actionChannels = colorChannels(action).slice(0, 3);
  const selectedTint = toHex(actionChannels.map((v) => 255 + (v - 255) * SELECTED_TINT_WEIGHT));
  // Brand TEXT sits on white, on the page ground and on the tint (selected
  // tiles, date tiles), so it keeps darkening past the action until it clears
  // 4.5:1 on all three. The fill stays the action.
  let text = action;
  if (!darkLabel) {
    let tl = l;
    while (
      tl > 0 &&
      (contrastRatio(text, tint) < 4.5 || contrastRatio(text, BRAND_PAGE_GROUND) < 4.5)
    ) {
      tl = Math.max(0, tl - 0.005);
      text = toHex(hslToRgb([h, s, tl]));
    }
  }
  return {
    action,
    hover: toHex(hslToRgb([h, s, Math.max(0, l - 0.04)])),
    pressed: toHex(hslToRgb([h, s, Math.max(0, l - 0.08)])),
    foreground: darkLabel ? BRAND_NEAR_BLACK : WHITE,
    tint,
    accent: darkLabel ? BRAND_NEAR_BLACK : action,
    text: darkLabel ? BRAND_NEAR_BLACK : text,
    selected: action,
    selectedTint,
    darkLabel,
  };
}

/**
 * The numeric ↔ semantic mapping — CON-10's required deliverable, as data
 * rather than as prose so it can be asserted and displayed.
 *
 * Two systems expand one nonprofit colour. This ramp emits SIX NUMERIC STEPS
 * (`accent-5` … `accent-60`); the email deriver
 * (`backends/messaging/src/emails/components/theme.ts` `resolveEmailBrandPalette`)
 * emits SIX SEMANTIC SLOTS. They are not the same six, and neither is a rename
 * of the other — there is no `accent-30` on this side and no `border` on that
 * one. The numeric names are the wire format and stay so: they cross the
 * `DONATE` postMessage boundary to the CharityStack hosted script, which lives
 * outside this repo, and are read as Tailwind `brand.*` utilities in three apps.
 *
 * This is what "one algorithm" means in practice under D-email-hardcodes: not
 * an import — a backend may not import frontend code — but a stated
 * correspondence, kept honest by the fixture table in
 * `packages/shared/src/styling/__tests__/brandContrast.test.js`, which CON-12
 * mirrors on the email side.
 *
 * Where the correspondence is approximate rather than equal, and why (CON-10
 * record §4 carries the per-hue numbers). `subtle` and `border` are the two
 * rows to be careful with: the email deriver reaches them by blending the brand
 * toward white, this ramp reaches them by pinning saturation and brightness in
 * HSB — so they agree on lightness and drift on chroma. The drift is largest at
 * the tint end, where this ramp pins saturation so low that `accent-10` and
 * `accent-5` are very nearly hue-INDEPENDENT: a warm neutral and a bright
 * yellow can expand to the same `accent-10`. That is `design.md` §6.10's third
 * bullet, and it is a ramp-SHAPE finding for CON-20 / CON-14 rather than
 * something a contrast guard can reach.
 *
 * `foreground` is the row CON-10 changed. The email side walks the brand toward
 * ink until it is readable; this side now does the same through
 * `brandTextForeground`, because the contrasting step is itself marginally under
 * the floor at some hues (`design.md` §6.10, first bullet) — so "use the
 * contrasting step for brand text" needs the guard, not just the step.
 *
 * Each row names the numeric `step` it is projected from, plus at most one
 * deriver: `derive` sees only that step's value, `derivePalette` sees the whole
 * palette (needed where the answer depends on more than one step).
 *
 * @type {Readonly<Record<string, { step: string, derive?: (value: string) => string, derivePalette?: (palette: Record<string, unknown>, options?: object) => string, role: string }>>}
 */
export const BRAND_SEMANTIC_MAPPING = Object.freeze({
  primary: { step: 'accent-50', role: "the primary call-to-action's fill" },
  contrastText: {
    step: 'accent-50',
    derive: contrastForeground,
    role: 'the label ON that fill',
  },
  foreground: {
    step: 'accent-60',
    // The ONLY row that needs the whole palette, and it must have it. Derived
    // per-step it applied the plain guard to the contrasting step, which for an
    // achromatic brand is the ramp's invented red — so this row gave a THIRD
    // answer, matching neither what the donor apps paint nor the email deriver
    // it corresponds to (which walks the brand itself toward ink, and so keeps
    // a grey brand grey). Since this structure is the fixture table CON-12
    // mirrors, that divergence would have travelled into branded email.
    derivePalette: brandTextFor,
    role:
      "brand-coloured TEXT on a neutral surface — the ramp's contrasting step where it is " +
      'hue-faithful to the brand, otherwise the fill step, in both cases past the readability guard',
  },
  foregroundContrastText: {
    step: 'accent-60',
    derive: contrastForeground,
    role: 'the label on that step when it is used as a fill',
  },
  subtle: {
    step: 'accent-10',
    role: 'the resting fill of a secondary or selected control',
  },
  border: {
    step: 'accent-20',
    role: "a brand-tinted edge; this ramp's nearest step is its tint-hover",
  },
});

/**
 * Brand-coloured TEXT for a palette, readable on the surface it sits on.
 *
 * Every call site that paints brand text wants the same thing and was writing
 * the same expression, so the rule lives here once. Two parts:
 *
 * **Which step.** `design.md` §1.5 gives the job to the ramp's contrasting
 * step, and §6.10 says it needs the guard on top. But the contrasting step is
 * not a darkening of the brand (see the note at the top of this file) — it is
 * the brand's HUE re-emitted at a pinned saturation, and an ACHROMATIC brand
 * has no hue for it to re-emit. Measured: every grey brand, black and white
 * included, expands to the same dark red, so routing grey brands through the
 * contrasting step would repaint a monochrome organisation's own text in a
 * colour it never chose. So the step is used only while it is hue-faithful to
 * the brand; otherwise the fill step is guarded directly, which keeps a grey
 * brand grey.
 *
 * **Then the guard**, which is what makes the result readable at any hue.
 *
 * @param {Record<string, unknown>} palette Keyed `accent-5` … `accent-60`.
 * @param {object} [options]
 * @param {string} [options.background] Surface the text sits on.
 * @param {number} [options.minRatio] Floor to clear.
 * @param {string} [options.fallback] Used when the palette carries neither
 *   step — typically the call site's own default brand hex.
 * @returns {string} A CSS colour string.
 */
export function brandTextFor(palette, options = {}) {
  const { fallback, ...guard } = options;
  const source = palette && typeof palette === 'object' ? palette : {};
  // Parseability is checked HERE, not left to the hue test. `hueOf` returns null
  // both for a colour that has no hue and for one it could not read, and
  // conflating those let an unparseable contrasting step beat a perfectly good
  // fill step — two nulls "share a hue", so the garbage won. Checkout's palette
  // comes off the DONATE wire, so an unreadable step is a real input shape.
  const fill = usableColor(source['accent-50']);
  const step = usableColor(source['accent-60']);
  const preferred = step && (!fill || sharesHue(fill, step)) ? step : fill || step;
  return brandTextForeground(preferred || fallback, guard);
}

/** The trimmed value if it is a colour this module can actually read, else null. */
function usableColor(value) {
  if (!isUsableColor(value)) return null;
  const trimmed = value.trim();
  try {
    colorChannels(trimmed);
    return trimmed;
  } catch {
    return null;
  }
}

/**
 * Do two colours read as the same hue?
 *
 * A chromatic colour never "shares" a hue with an achromatic one — that is the
 * case this exists to catch. Two colours that are BOTH hueless do share the
 * absence of one, which is why the null branch compares rather than rejecting:
 * a grey brand whose contrasting step also came back grey should keep using
 * that step.
 *
 * Unparseable values never reach here — {@link brandTextFor} filters them out
 * first, precisely because they would ALSO be null and would then "share a hue"
 * with a grey brand.
 */
function sharesHue(left, right) {
  const a = hueOf(left);
  const b = hueOf(right);
  if (a === null || b === null) return a === b;
  const gap = Math.abs(a - b);
  return Math.min(gap, 360 - gap) <= 15;
}

/**
 * Hue in degrees, or null when the colour is achromatic.
 *
 * Parsing goes through the contrast module's own parser rather than a local hex
 * regex, so an `rgb()` step — the shape checkout's wire palette can carry — is
 * read rather than silently treated as hueless. A local hex-only regex made
 * BOTH steps of an `rgb()` palette return null, `sharesHue` then said they
 * matched, and a monochrome nonprofit's text was painted the ramp's invented
 * red: the exact failure the achromatic branch exists to prevent.
 */
function hueOf(value) {
  let red;
  let green;
  let blue;
  try {
    [red, green, blue] = colorChannels(value);
  } catch {
    return null;
  }
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;
  // The ramp's own saturation floor is 1%, so anything under ~2/255 of spread
  // is a colour the ramp cannot carry a hue through.
  if (delta <= 2) return null;
  let hue;
  if (max === red) hue = ((green - blue) / delta) % 6;
  else if (max === green) hue = (blue - red) / delta + 2;
  else hue = (red - green) / delta + 4;
  hue = Math.round(hue * 60);
  return hue < 0 ? hue + 360 : hue;
}

/**
 * Project a numeric palette onto the semantic slots above.
 *
 * The donor apps never call this — they read the numeric ramp, which is the
 * wire format. It exists so the mapping is executable: the fixture table CON-12
 * mirrors is generated from it, and the gallery displays it, so a mapping that
 * drifts from the ramp fails a test rather than going stale in a comment.
 *
 * @param {Record<string, unknown>} palette Keyed `accent-5` … `accent-60`.
 * @returns {Record<string, string>} Slot → colour, omitting any slot whose
 *   source step is missing from the palette.
 */
export function resolveSemanticSlots(palette) {
  if (!palette || typeof palette !== 'object') return {};
  const slots = {};
  for (const [slot, { step, derive, derivePalette }] of Object.entries(BRAND_SEMANTIC_MAPPING)) {
    const value = palette[step];
    if (!isUsableColor(value)) continue;
    if (derivePalette) slots[slot] = derivePalette(palette, { fallback: value.trim() });
    else slots[slot] = derive ? derive(value.trim()) : value.trim();
  }
  return slots;
}

/**
 * The palette keys the generator emits, mapped to the ramp property each one
 * writes. Note there is no `accent-30`: the HSB ramp skips it.
 * @type {ReadonlyArray<[string, string]>}
 */
export const BRAND_RAMP_KEYS = Object.freeze([
  ['accent-60', '--brand-60'],
  ['accent-50', '--brand-50'],
  ['accent-40', '--brand-40'],
  ['accent-20', '--brand-20'],
  ['accent-10', '--brand-10'],
  ['accent-5', '--brand-5'],
]);

/** The semantic properties derived from the ramp. */
export const BRAND_SEMANTIC_PROPERTIES = Object.freeze([
  '--cs-color-primary',
  '--cs-color-primary-foreground',
  '--cs-color-primary-dark',
  '--cs-brand-text',
  // Donor redesign roles (CON-512) — see deriveBrandRoles.
  '--cs-brand-action',
  '--cs-brand-action-hover',
  '--cs-brand-action-pressed',
  '--cs-brand-foreground',
  '--cs-brand-tint',
  '--cs-brand-accent',
  // The selected choice tile's edge and fill (rule 7 of deriveBrandRoles).
  '--cs-brand-selected',
  '--cs-brand-selected-tint',
  '--cs-banner-bg',
  '--cs-control-hover-bg',
  // Tier-2 dials whose value depends on the brand. Declared in donor.css /
  // portal.css with the no-brand value; a var() there resolves at :root, so
  // the branded value has to be written here, on the scope element.
  '--cs-input-focus-ring',
  '--cs-input-focus-shadow',
  '--cs-focus-outline',
]);

/** Every custom property this module may write. */
export const BRAND_TOKEN_PROPERTIES = Object.freeze([
  ...BRAND_RAMP_KEYS.map(([, property]) => property),
  ...BRAND_SEMANTIC_PROPERTIES,
]);

function isUsableColor(value) {
  return typeof value === 'string' && value.trim() !== '';
}

/**
 * Turn a palette (or a base colour plus an expander) into the property map.
 *
 * @param {object} input
 * @param {Record<string, unknown>} [input.palette] Already-expanded palette,
 *   keyed `accent-5` … `accent-60`. Unrecognised keys are ignored.
 * @param {string} [input.color] Base brand hex. Requires `expand`.
 * @param {(hex: string) => Record<string, unknown>} [input.expand] The ramp
 *   generator. Passed in rather than imported so this package stays dep-free —
 *   `packages/shared` binds `generateBrandColors` (HSB.js, unchanged in M1).
 * @returns {Record<string, string>} Property → value. Empty when there is
 *   nothing trustworthy to write.
 */
export function resolveBrandTokens({ palette, color, expand } = {}) {
  let source = palette;

  if (!source && isUsableColor(color)) {
    if (typeof expand !== 'function') {
      // Not a throw: a surface with a colour but no expander should render
      // unbranded (static defaults), not crash a donation page.
      warnOnce(
        'resolveBrandTokens: `color` was supplied without `expand`; ' +
          'pass generateBrandColors (from @charitystack/shared) as `expand`, ' +
          'or pass an already-expanded `palette`.',
      );
      return {};
    }
    try {
      source = expand(color);
    } catch (error) {
      warnOnce('resolveBrandTokens: the expander threw for ' + color, error);
      return {};
    }
  }

  if (!source || typeof source !== 'object') return {};

  const tokens = {};
  for (const [key, property] of BRAND_RAMP_KEYS) {
    const value = source[key];
    if (isUsableColor(value)) tokens[property] = value.trim();
  }

  // Semantic properties. Each is only written when its ramp source exists, so a
  // partial palette degrades to the stylesheet defaults rather than to the
  // string "undefined".
  // The org colour is the ramp's base step (accent-50 is the hex itself). The
  // donor redesign roles are derived from it alone (CON-512); the legacy names
  // are written as aliases of those roles so existing readers follow the new
  // rule until M2/M3 move them onto the role names.
  const base = tokens['--brand-50'];
  if (base) {
    const roles = deriveBrandRoles(base);
    tokens['--cs-brand-action'] = roles.action;
    tokens['--cs-brand-action-hover'] = roles.hover;
    tokens['--cs-brand-action-pressed'] = roles.pressed;
    tokens['--cs-brand-foreground'] = roles.foreground;
    tokens['--cs-brand-tint'] = roles.tint;
    tokens['--cs-brand-accent'] = roles.accent;
    tokens['--cs-brand-text'] = roles.text;
    tokens['--cs-brand-selected'] = roles.selected;
    tokens['--cs-brand-selected-tint'] = roles.selectedTint;
    tokens['--cs-banner-bg'] = roles.action;
    tokens['--cs-input-focus-ring'] = roles.accent;
    tokens['--cs-input-focus-shadow'] = `inset 0 0 0 1px ${roles.accent}`;
    tokens['--cs-focus-outline'] = `2px solid ${roles.accent}`;
    // Legacy aliases.
    tokens['--brand-50'] = roles.action;
    tokens['--brand-10'] = roles.tint;
    tokens['--cs-color-primary'] = roles.action;
    tokens['--cs-color-primary-foreground'] = roles.foreground;
    tokens['--cs-color-primary-dark'] = roles.hover;
    tokens['--cs-control-hover-bg'] = roles.tint;
  }

  return tokens;
}

// Per-element write counter. Two providers can legitimately target the same
// element (a page that mounts a widget inside a branded shell), and the first
// one to unmount must not strip the properties out from under the second.
// Cleanup only removes a property once the last writer has released it.
const writerCounts = typeof WeakMap === 'function' ? new WeakMap() : null;

function retain(element, properties) {
  if (!writerCounts) return;
  let counts = writerCounts.get(element);
  if (!counts) {
    counts = new Map();
    writerCounts.set(element, counts);
  }
  for (const property of properties) counts.set(property, (counts.get(property) || 0) + 1);
}

function release(element, property) {
  if (!writerCounts) return true;
  const counts = writerCounts.get(element);
  if (!counts) return true;
  const next = (counts.get(property) || 1) - 1;
  if (next > 0) {
    counts.set(property, next);
    return false;
  }
  counts.delete(property);
  return true;
}

/**
 * Write the tokens onto one element.
 *
 * @param {HTMLElement|null|undefined} element The scope. MUST NOT be
 *   `document.documentElement` (D6) — that is rejected, loudly in development.
 * @param {Record<string, string>} tokens Output of {@link resolveBrandTokens}.
 * @returns {() => void} A cleanup that removes exactly the properties written.
 */
export function applyBrandTokens(element, tokens) {
  const noop = () => {};
  if (!element || typeof element.style?.setProperty !== 'function') return noop;

  if (
    typeof document !== 'undefined' &&
    (element === document.documentElement || element === document)
  ) {
    warnOnce(
      'applyBrandTokens: refusing to write brand tokens to document.documentElement. ' +
        'The brand provider is scoped (D6) — pass the surface container instead. ' +
        'See docs/initiatives/new/design-system/decisions.md#d6.',
    );
    return noop;
  }

  const written = [];
  for (const [property, value] of Object.entries(tokens || {})) {
    if (!BRAND_TOKEN_PROPERTIES.includes(property)) continue;
    if (!isUsableColor(value)) continue;
    element.style.setProperty(property, value);
    written.push(property);
  }
  retain(element, written);

  let released = false;
  return () => {
    if (released) return;
    released = true;
    for (const property of written) {
      if (release(element, property)) element.style.removeProperty(property);
    }
  };
}

/** Remove every brand property this module can write, from one element. */
export function clearBrandTokens(element) {
  if (!element || typeof element.style?.removeProperty !== 'function') return;
  for (const property of BRAND_TOKEN_PROPERTIES) element.style.removeProperty(property);
}

// --- development-only surface audit (D16) --------------------------------
//
// A `var()` read cannot be hooked — no browser API exposes one, and the
// fallback arm is invisible to JS (decisions.md D16). The implementable check
// is the other way round: after the provider has run, look at what the branded
// selectors actually resolved to, and warn when they resolved to the static
// default, i.e. when nothing branded them.

/** The value elements' shared-styles/index.css declares when nothing brands it. */
export const UNBRANDED_PRIMARY_DEFAULT = '#69995E';

/**
 * Does a resolved `--cs-color-primary` mean "nothing branded this"? (CON-528)
 *
 * @param {string} resolved
 * @returns {boolean}
 */
export function isUnbrandedPrimary(resolved) {
  const value = String(resolved || '').trim().toLowerCase();
  if (value === '') return true;
  return (
    value === UNBRANDED_PRIMARY_DEFAULT.toLowerCase() ||
    value === deriveBrandRoles(UNBRANDED_PRIMARY_DEFAULT).action.toLowerCase()
  );
}

const warned = new Set();
function warnOnce(message, ...rest) {
  if (warned.has(message)) return;
  warned.add(message);
  console.warn(message, ...rest);
}

/**
 * Is the brand audit allowed to run?
 *
 * Follows the house dev-guard pattern
 * (`packages/shared/src/utils/subscriptionFeePolicy.js:82-101`) for two reasons
 * that are easy to get wrong:
 *
 *  - Every donor app is a Vite browser build, where `process` is UNDEFINED.
 *    A `typeof process !== 'undefined'` guard is therefore permanently false in
 *    browser dev and the check silently never fires. `import.meta.env.DEV` is
 *    the dev signal there; NODE_ENV covers node/vitest.
 *  - It is FAIL-CLOSED. `NODE_ENV !== 'production'` is true when NODE_ENV is
 *    merely UNSET, which would let a donor-facing surface run a
 *    querySelectorAll + getComputedStyle sweep on every palette change. An
 *    absent signal must mean "not dev", never "probably dev".
 *
 * @returns {boolean}
 */
export function isBrandAuditEnabled() {
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      // Vite statically replaces this, so a production build folds to `false`.
      return import.meta.env.DEV === true || import.meta.env.MODE === 'development';
    }
  } catch {
    /* not an ESM/Vite context — fall through to process.env */
  }
  try {
    return (
      typeof process !== 'undefined' &&
      process?.env?.NODE_ENV === 'development'
    );
  } catch {
    return false;
  }
}

/**
 * Dev-only: warn when a branded selector resolved to the static default.
 *
 * The body cannot run in production: {@link isBrandAuditEnabled} is fail-closed
 * and its Vite arm folds to `false` in a production build. (It is a runtime
 * function with a try/catch, so do NOT claim the bundler eliminates the body —
 * it is present in the bundle and simply never entered.)
 *
 * The provider calls this for the surface it wraps. A document with NO provider
 * mounted at all has nothing to trigger it, which is inherent to the mechanism —
 * so this is exported for an app to call directly from its own dev entrypoint.
 *
 * @param {HTMLElement} [root] Where to look. Defaults to `document.body`.
 * @param {string[]} [selectors] Branded selectors to sample.
 * @returns {number} How many unbranded elements were found (0 when disabled).
 */
export function auditBrandedSurfaces(root, selectors) {
  if (!isBrandAuditEnabled()) return 0;
  if (typeof window === 'undefined' || typeof document === 'undefined') return 0;

  const scope = root || document.body;
  if (!scope || typeof scope.querySelectorAll !== 'function') return 0;

  const probes = selectors || ['.bg-primary', '.text-primary', '[data-cs-branded]'];
  const offenders = [];

  for (const selector of probes) {
    let nodes;
    try {
      nodes = scope.querySelectorAll(selector);
    } catch {
      continue;
    }
    for (const node of nodes) {
      const resolved = window
        .getComputedStyle(node)
        .getPropertyValue('--cs-color-primary')
        .trim();
      // Unset (the provider never ran and no stylesheet declared it), still
      // the hardcoded legacy green (declared, never overwritten), or the sage's
      // derived action role — what a surface that branded itself with the sage
      // stand-in for "no colour" resolves to since the roles landed (CON-528).
      if (isUnbrandedPrimary(resolved)) {
        offenders.push({ selector, node });
      }
    }
  }

  if (offenders.length > 0) {
    console.warn(
      `[design-tokens] ${offenders.length} branded element(s) resolved --cs-color-primary to the ` +
        'unbranded default (or the sage stand-in for "no colour"). They are outside a ' +
        'BrandTokenProvider scope, the provider had no palette when they painted, or a sage ' +
        'fallback was fed to it in place of no colour.',
      offenders,
    );
  }
  return offenders.length;
}
